/**
 * The new console's navigation, per role, from the v109 handoff (§4 "Role Navigation & High-Level Permissions").
 *
 * One application, role-based rendering (§2). Every role sees the same shell; this table says which entries each
 * role gets. An entry points at a page under /lp-console (the staging root until the 26 Sep 2026 swap). Pages from the old console
 * are mounted there unchanged (the handoff: preserve accepted screens, connect them, do not recreate). Entries the
 * handoff names but does not yet specify are `pending`, shown greyed with the section that owes the spec.
 *
 * Talent's shell is "to be finalized next" (§12); the talent_coordinator role gets the locked rules only.
 */
/** The console's root. Named for where it was built; since the 26 Sep 2026 swap it is the console itself. */
export const STAGING_ROOT = '/lp-console'

export const ROLES = ['admin', 'sales', 'billing', 'editing', 'talent_coordinator']

const item = (key, title, group, opts = {}) => ({ key, title, group, path: `${STAGING_ROOT}/${opts.slug || key}`, status: opts.status || 'live', note: opts.note || null, external: !!opts.external })

/** Every entry the new console has, in the handoff's groups and order. */
export const NAV_ITEMS = [
  item('dashboard', 'Dashboard', 'Core'),
  item('bookings', 'Bookings', 'Core'),
  item('billing', 'Billing', 'Core'),
  item('clients', 'Clients', 'Core'),
  item('talent', 'Talent', 'Core'),
  item('coordination', 'Coordination', 'Core', { note: "Nautica's workbench (#445–#456): Action Center, queues, clusters, talent list. Already the new Talent-side build." }),
  item('messages', 'Messages', 'Core'),
  item('resources', 'Resources', 'Core', { note: 'Quick links to each department\'s external tools; open in a new tab (prototype v108 §7).' }),
  item('notifications', 'Notification Center', 'Core', { note: 'Flag / System model is §7; the catalog is still owed (§18). Reads today\'s role notifications.' }),
  item('insurance', 'Insurance Requests', 'Operations'),
  item('referrals', 'Referrals', 'Operations'),
  item('reviews', 'Reviews', 'Operations', { note: 'Reconciliation with the preserved review architecture is open (§13, §18).' }),
  item('controls', 'Business Controls', 'Business', { note: 'Pricing, booking rules, complimentary services, coupons: the live pages consolidated (v108).' }),
  item('reports', 'Reports & Analytics', 'Business', { note: "Traci's executive page (22 Sep 2026). Every number from the same tables and balance view as Billing." }),
  item('users', 'Users & Roles', 'Settings'),
  item('audit', 'Audit Log', 'Settings', { note: 'Admin-only, immutable (§15).' })
]

/** §4 table: which entries each role sees. */
export const ROLE_NAV = {
  admin: ['dashboard', 'bookings', 'clients', 'talent', 'coordination', 'messages', 'resources', 'notifications', 'insurance', 'referrals', 'reviews', 'controls', 'reports', 'users', 'audit', 'billing'],
  sales: ['dashboard', 'bookings', 'clients', 'messages', 'resources', 'notifications'],
  billing: ['dashboard', 'bookings', 'clients', 'talent', 'messages', 'resources', 'notifications', 'referrals', 'billing'],
  // Traci, 28 Sep 2026: Editing opens Clients; the Talent department opens Insurance Requests.
  editing: ['dashboard', 'bookings', 'clients', 'talent', 'messages', 'resources', 'notifications'],
  // Talent shell not finalized (§12): the locked rules say no Billing, Talent-side messaging, the four resources.
  talent_coordinator: ['dashboard', 'bookings', 'talent', 'coordination', 'messages', 'resources', 'notifications', 'insurance']
}

export const GROUP_ORDER = ['Core', 'Operations', 'Business', 'Settings']

/**
 * Pages a role may open but that are not in its sidebar. Coordination (Traci, 28 Sep 2026: "the dashboard vs
 * coordination tab is a bit redundant"): the workbench is Nautica's Dashboard now, and Admin doesn't need the tab
 * either. Its pages (a booking's day plan, /coordination itself) still open from the cards and Admin's Talent view.
 */
export const SIDEBAR_HIDDEN = { talent_coordinator: ['coordination'], admin: ['coordination'] }

/** The sidebar for a role: groups in order, each with its visible entries in NAV_ITEMS order. */
export function navFor(role) {
  const hidden = new Set(SIDEBAR_HIDDEN[role] || [])
  const keys = new Set((ROLE_NAV[role] || []).filter(k => !hidden.has(k)))
  const groups = GROUP_ORDER.map(group => ({ group, items: NAV_ITEMS.filter(i => i.group === group && keys.has(i.key)) }))
  return groups.filter(g => g.items.length > 0)
}

/**
 * May any of these roles open this entry? `roles` is a Set (as the server's collectUserRoles returns) or an array.
 * The top-bar search called .some() on the Set and failed for everyone (29 Sep 2026).
 */
export function canOpenAny(roles, key) {
  return [...(roles || [])].some(r => canOpen(r, key))
}

/** May this role open this entry? Used by the route guard as well as the sidebar (§2: enforce, do not only hide). */
export function canOpen(role, key) {
  return (ROLE_NAV[role] || []).includes(key)
}

/**
 * Pages reached from inside the console rather than the sidebar, and who may open them (25 Sep 2026). The Editing
 * board is Editing's own dashboard; Admin opens it from Department Actions. My Profile is everyone's.
 */
export const OFF_NAV = {
  editing: ['admin', 'editing'],
  'editing-preview': ['admin', 'editing'],   // old address of the pipeline, redirects to editing
  profile: ROLES,
  // Old-console pages with no place in the new one yet (swap, 26 Sep 2026): Leads and the Testing tools, Admin only
  sweepstakes: ['admin'],
  testing: ['admin']
}

/** May this role open this path under the staging root? The route guard's one rule, sidebar entries and off-nav pages alike. */
export function canOpenPath(role, pathname) {
  const rest = String(pathname || '').replace(/\/+$/, '').slice(STAGING_ROOT.length).replace(/^\//, '')
  const first = rest.split('/')[0] || 'dashboard'
  if (OFF_NAV[first]) return OFF_NAV[first].includes(role)
  if (NAV_ITEMS.some(i => i.key === first)) return canOpen(role, first)
  return false
}

/**
 * Old addresses (bookmarks, emails, the staging period's links) forward to the page that took their place, with the
 * rest of the path and the query kept: /lp-console/staging/talent/1 → /lp-console/talent/1.
 */
export const FORWARDS = {
  staging: '',
  'booking-attempts': 'controls?tab=rules',
  'insurance-contracts': 'insurance',
  'post-event-addons': 'editing',
  'addon-pricing': 'controls?tab=pricing',
  promotions: 'controls?tab=coupons',
  'contact-compliance': 'coordination',
  analytics: 'reports',
  'users-roles': 'users',
  'audit-log': 'audit',
  'talent-coordinator': 'coordination'
}

export function forwardPath(pathname, search = '') {
  const rest = pathname.replace(/^\/lp-console\/?/, '').split('/')
  const target = FORWARDS[rest[0]] ?? ''
  const [base, query] = target.split('?')
  const tail = rest[0] === 'staging' ? rest.slice(1) : []
  const path = [base, ...tail].filter(Boolean).join('/') || 'dashboard'
  const q = [query, search.replace(/^\?/, '')].filter(Boolean).join('&')
  return `${STAGING_ROOT}/${path}${q ? `?${q}` : ''}`
}

/** The entry a pathname under the staging root belongs to, or null. */
export function navItemFor(pathname) {
  const rest = String(pathname || '').replace(/\/+$/, '').slice(STAGING_ROOT.length).replace(/^\//, '')
  if (rest === '') return NAV_ITEMS[0]
  const first = rest.split('/')[0]
  return NAV_ITEMS.find(i => i.key === first) || null
}

/** Topbar title (§3 "dynamic title"). */
export function pageTitleFor(pathname) {
  const it = navItemFor(pathname)
  return it ? it.title : 'Love & Photos'
}

/** The handoff's role labels as staff read them. */
export const ROLE_LABELS = { admin: 'Admin', sales: 'Sales', billing: 'Billing', editing: 'Editing', talent_coordinator: 'Talent' }
